import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { mattermostService, type MMChannel, type MMTeam } from '../../services/mattermostService';

export function MattermostChannelsPage() {
  const [teams, setTeams] = useState<MMTeam[]>([]);
  const [teamId, setTeamId] = useState('');
  const [channels, setChannels] = useState<MMChannel[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, ch] = await Promise.all([
        mattermostService.teams(),
        mattermostService.channels(teamId || undefined),
      ]);
      setTeams(t);
      setChannels(ch);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا');
      setChannels([]);
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="space-y-4" dir="rtl">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">کانال‌های Mattermost</h1>
          <p className="mt-1 text-sm text-slate-500">فقط کانال‌هایی که حساب یکپارچه‌سازی عضو آن‌هاست.</p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          className="inline-flex items-center gap-2 rounded-lg border bg-white px-4 py-2 text-sm font-semibold"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          تازه‌سازی
        </button>
      </div>

      <label className="block max-w-sm text-sm">
        تیم
        <select
          className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2"
          value={teamId}
          onChange={(e) => setTeamId(e.target.value)}
        >
          <option value="">همه تیم‌ها</option>
          {teams.map((t) => (
            <option key={t.id} value={t.id}>
              {t.display_name || t.displayName || t.name}
            </option>
          ))}
        </select>
      </label>

      <div className="overflow-x-auto rounded-xl bg-white shadow-sm">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="px-4 py-3 text-right">نام</th>
              <th className="px-4 py-3 text-right">نوع</th>
              <th className="px-4 py-3 text-right">تیم</th>
              <th className="px-4 py-3 text-right">آخرین پیام</th>
              <th className="px-4 py-3 text-right">تعداد</th>
              <th className="px-4 py-3 text-right" />
            </tr>
          </thead>
          <tbody>
            {loading && channels.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                  در حال بارگذاری…
                </td>
              </tr>
            ) : channels.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                  کانالی نیست
                </td>
              </tr>
            ) : (
              channels.map((c) => (
                <tr key={c.id} className="border-t border-slate-100">
                  <td className="px-4 py-3 font-medium">{c.displayName || c.name}</td>
                  <td className="px-4 py-3">{c.type}</td>
                  <td className="px-4 py-3">{c.teamName || '—'}</td>
                  <td className="px-4 py-3">
                    {c.lastPostAt ? new Date(c.lastPostAt).toLocaleString('fa-IR') : '—'}
                  </td>
                  <td className="px-4 py-3">{c.messageCount ?? '—'}</td>
                  <td className="px-4 py-3">
                    <Link
                      to={`/mattermost/messages?channelId=${encodeURIComponent(c.id)}`}
                      className="text-teal-700 hover:underline"
                    >
                      پیام‌ها
                    </Link>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
